import { flexRender } from '@tanstack/react-table';

import { Flex } from 'shared_DEPRECATED/components/Flex';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import Spacer from 'shared_DEPRECATED/components/Spacer';

import { useParticipantsTable } from 'features/participants/hooks';

import { Typography } from 'shared/components/Typography';

import styles from './ParticipantsTable.module.css';

const TableHeaderContent = ({
  canSort,
  isSorted,
  isSortedDesc,
  children,
  onClick,
}: {
  canSort: boolean;
  isSorted: boolean;
  isSortedDesc: boolean;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
}) => (
  <Spacer size="lg lg lg zr">
    <div onClick={onClick} style={{ cursor: canSort ? 'pointer' : 'default' }}>
      <Flex
        justifyContent="flex-start"
        dataTestid="participants-table-header"
        gap="0.5rem"
        alignItems="center"
      >
        <Typography
          fontWeight="medium"
          type="small"
          whiteSpace="nowrap"
          color="gray"
        >
          {children}
        </Typography>
        {canSort && (
          <Icon
            name={
              isSorted && !isSortedDesc
                ? iconNames.chevronUp
                : iconNames.chevronDown
            }
            width="8px"
            stroke={isSorted ? 'var(--fgPrimary)' : 'var(--fgSecondary)'}
          />
        )}
      </Flex>
    </div>
  </Spacer>
);

const TableCellContent = ({
  children,
  isLastCell,
}: {
  children: React.ReactNode;
  isLastCell: boolean;
}) => (
  <Spacer size={`lg ${isLastCell ? 'zr' : 'lg'} lg zr`}>
    <Spacer size={`zr ${isLastCell ? 'zr' : 'lg'} zr zr`}>{children}</Spacer>
  </Spacer>
);

const ParticipantsTable = ({
  tableProps,
  onChatOpen,
  isChatOpen,
}: {
  tableProps: ReturnType<typeof useParticipantsTable>;
  onChatOpen?: (email: string, message?: string) => void;
  isChatOpen: boolean;
}) => {
  const table = tableProps;

  return (
    <table className={styles['participants-table']}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className={styles.row}>
            {headerGroup.headers.map((header) => (
              <th key={header.id}>
                <TableHeaderContent
                  canSort={header.column.getCanSort()}
                  isSorted={!!header.column.getIsSorted()}
                  isSortedDesc={header.column.getIsSorted() === 'desc'}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHeaderContent>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr
            key={row.id}
            className={styles.row}
            data-testid="participants-table-row"
          >
            {row.getVisibleCells().map((cell, cellIndex) => (
              <td key={cell.id}>
                <TableCellContent
                  isLastCell={cellIndex === row.getVisibleCells().length - 1}
                >
                  {flexRender(cell.column.columnDef.cell, {
                    ...cell.getContext(),
                    table: {
                      ...cell.getContext().table,
                      options: {
                        ...cell.getContext().table.options,
                        meta: { onChatOpen, isChatOpen },
                      },
                    },
                  })}
                </TableCellContent>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default ParticipantsTable;
